import React from 'react'
import styled from 'styled-components'
import { LandUsePlanDocument } from '@popety-io/types'

export interface DocumentsListTranslations {
  noDocuments?: string
  view?: string
}

const DEFAULT_TRANSLATIONS: DocumentsListTranslations = {
  noDocuments: 'No documents available',
  view: 'View',
}

interface DocumentsListProps {
  documents: LandUsePlanDocument[]
  onViewDocument: (doc: LandUsePlanDocument) => void
  translations?: DocumentsListTranslations
}

export const DocumentsList: React.FC<DocumentsListProps> = ({
  documents,
  onViewDocument,
  translations,
}) => {
  const t = { ...DEFAULT_TRANSLATIONS, ...translations }

  if (documents.length === 0) {
    return <EmptyState>{t.noDocuments}</EmptyState>
  }

  return (
    <ListContainer>
      {documents.map((doc) => (
        <DocumentCard key={doc.id} onClick={() => onViewDocument(doc)}>
          <DocumentInfo>
            <DocumentTitle title={doc.title}>{doc.title}</DocumentTitle>
            <DocumentMeta>
              {doc.plan_type_granularity &&
                doc.plan_type_granularity.charAt(0).toUpperCase() +
                  doc.plan_type_granularity.slice(1)}
              {doc.plan_type_granularity && doc.document_type && ' · '}
              {doc.document_type &&
                doc.document_type.charAt(0).toUpperCase() +
                  doc.document_type.slice(1)}
            </DocumentMeta>
          </DocumentInfo>
          <ViewLink>{t.view} ›</ViewLink>
        </DocumentCard>
      ))}
    </ListContainer>
  )
}

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
  margin-bottom: ${({ theme }) => theme.spacing.default};
`

const DocumentCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }
`

const DocumentInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const DocumentTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const DocumentMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-top: 2px;
`

const ViewLink = styled.span`
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.primary};
`

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing.large};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
`
